import React, { useEffect, Suspense, lazy, createElement } from 'react';
import { SocialProof } from './components/SocialProof';
import { PainPoints } from './components/PainPoints';
const ProductShowcase = lazy(() => import('./components/ProductShowcase').then(m => ({
  default: m.ProductShowcase
})));
const WhatYouGet = lazy(() => import('./components/WhatYouGet').then(m => ({
  default: m.WhatYouGet
})));
const Bonuses = lazy(() => import('./components/Bonuses').then(m => ({
  default: m.Bonuses
})));
const Countdown = lazy(() => import('./components/Countdown').then(m => ({
  default: m.Countdown
})));
const Pricing = lazy(() => import('./components/Pricing').then(m => ({
  default: m.Pricing
})));
const ProBusiness = lazy(() => import('./components/ProBusiness').then(m => ({
  default: m.ProBusiness
})));
const Guarantee = lazy(() => import('./components/Guarantee').then(m => ({
  default: m.Guarantee
})));
const Testimonials = lazy(() => import('./components/Testimonials').then(m => ({
  default: m.Testimonials
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const DarkCTA = lazy(() => import('./components/DarkCTA').then(m => ({
  default: m.DarkCTA
})));
const FinalCTA = lazy(() => import('./components/FinalCTA').then(m => ({
  default: m.FinalCTA
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const PurchaseNotifications = lazy(() => import('./components/PurchaseNotifications').then(m => ({
  default: m.PurchaseNotifications
})));
export function App() {
  useEffect(() => {
    document.title = 'Megapack Estimulación Temprana — Construye Progreso™';
    document.documentElement.lang = 'es';
    const setMeta = (name: string, content: string, property?: boolean) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };
    const description = 'Sistema completo de estimulación temprana de 0 a 36 meses. 6 Tomos, 144 sesiones listas para usar, fichas imprimibles y checklists. Todo tu año de estimulación resuelto. Acceso inmediato.';
    const url = 'https://estimulatemprano.construyeprogreso.com';
    const image = 'https://cdn.magicpatterns.com/uploads/v7HbDBttB6KwqMN8KX8fXz/hf_20260408_171512_ee149707-ab8e-4b03-a52c-19e854e7886e_(1)-Photoroom.webp';
    setMeta('description', description);
    setMeta('keywords', 'estimulación temprana, megapack, 0 a 36 meses, sesiones de estimulación, neurodesarrollo, fichas imprimibles, profesionales, Argentina, construye progreso');
    setMeta('robots', 'index, follow');
    setMeta('author', 'Construye Progreso™');
    setMeta('viewport', 'width=device-width, initial-scale=1');
    if (!document.querySelector('link[rel="canonical"]')) {
      const canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = url;
      document.head.appendChild(canonical);
    }
    setMeta('og:title', 'Megapack Estimulación Temprana — Todo tu año resuelto', true);
    setMeta('og:description', description, true);
    setMeta('og:image', image, true);
    setMeta('og:url', url, true);
    setMeta('og:type', 'product', true);
    setMeta('og:locale', 'es_AR', true);
    setMeta('og:site_name', 'Construye Progreso™', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'Megapack Estimulación Temprana — Construye Progreso™');
    setMeta('twitter:description', description);
    setMeta('twitter:image', image);
    if (!document.querySelector('script[data-ld="product"]')) {
      const ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.setAttribute('data-ld', 'product');
      ld.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Megapack Estimulación Temprana — Método Integral™',
        description: description,
        image: image,
        brand: {
          '@type': 'Brand',
          name: 'Construye Progreso™'
        },
        offers: {
          '@type': 'AggregateOffer',
          lowPrice: '19990',
          highPrice: '27990',
          priceCurrency: 'ARS',
          availability: 'https://schema.org/InStock',
          offerCount: 2
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '847'
        }
      });
      document.head.appendChild(ld);
    }
    if (!document.querySelector('script[data-ld="org"]')) {
      const orgLd = document.createElement('script');
      orgLd.type = 'application/ld+json';
      orgLd.setAttribute('data-ld', 'org');
      orgLd.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Construye Progreso™',
        url: url,
        logo: image,
        email: 'construyeprogresoo@gmail.com',
        sameAs: ['https://www.instagram.com/construyeprogresoo']
      });
      document.head.appendChild(orgLd);
    }
    if (!document.querySelector('script[data-ld="faq"]')) {
      const faqLd = document.createElement('script');
      faqLd.type = 'application/ld+json';
      faqLd.setAttribute('data-ld', 'faq');
      faqLd.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [{
          '@type': 'Question',
          name: '¿Qué incluye el Megapack de Estimulación Temprana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '6 Tomos completos (0 a 36 meses), 144 sesiones listas para usar, fichas imprimibles, checklists de seguimiento, guiones para familias y 6 bonos profesionales.'
          }
        }, {
          '@type': 'Question',
          name: '¿Cómo recibo el material?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Todo es 100% digital en formato PDF. Recibís acceso inmediato en tu email dentro de los 5 minutos posteriores a la compra.'
          }
        }, {
          '@type': 'Question',
          name: '¿Tiene garantía?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sí, ofrecemos garantía de 7 días sin preguntas. Si no te sirve, te devolvemos el 100% de tu dinero.'
          }
        }]
      });
      document.head.appendChild(faqLd);
    }
  }, []);
  useEffect(() => {
    // Defer Facebook Pixel loading to after page is interactive
    const loadFbPixel = () => {
      if (typeof window !== 'undefined' && !(window as any).fbq) {
        const f = window as any;
        const n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        (window as any).fbq('init', '1215304437119773');
        (window as any).fbq('track', 'PageView');
        const t = document.createElement('script');
        t.async = true;
        t.src = 'https://connect.facebook.net/en_US/fbevents.js';
        const s = document.getElementsByTagName('script')[0];
        s?.parentNode?.insertBefore(t, s);
      }
    };
    // Use requestIdleCallback to defer FB pixel, fallback to setTimeout
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(loadFbPixel, {
        timeout: 3000
      });
    } else {
      setTimeout(loadFbPixel, 2000);
    }
  }, []);
  return <>
      <noscript data-id="element-0">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=1215304437119773&ev=PageView&noscript=1" alt="" data-id="element-1" />
      </noscript>

      <header className="relative flex w-full flex-col items-center justify-center text-center text-gray-900 text-base leading-6 px-5 sm:px-6 py-12 sm:py-16" role="banner" style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      backgroundImage: 'linear-gradient(135deg, rgb(240,249,255) 0%, rgb(224,242,254) 30%, rgb(240,249,255) 60%, rgb(248,250,252) 100%)',
      backgroundColor: 'rgb(255, 255, 255)'
    }} data-id="element-2">
        {/* ── 1. BADGE ── */}
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-cyan-brand/30 bg-white/80 px-4 py-1.5 shadow-sm" data-id="element-3">
          <span className="text-xs sm:text-sm font-bold text-cyan-brand tracking-wide uppercase" data-id="element-4">
            🧠 Sistema Clínico Profesional · 0 a 36 meses
          </span>
        </div>

        {/* ── 2. H1 SOLO ── */}
        <h1 className="sm:text-5xl md:text-[72px] sm:leading-tight md:leading-[80px] font-black max-w-4xl text-gray-900 m-0 mb-2 text-[34px] leading-[42px]" data-id="element-5">
          Dejá de improvisar
          <br data-id="element-6" />
          tus sesiones.
          <br data-id="element-7" />
          <span className="text-cyan-brand" data-id="element-8">Empezá a ejercer.</span>
        </h1>

        {/* ── 3. MOCKUP ── */}
        <div className="my-6 sm:my-8 w-full max-w-[320px] sm:max-w-[380px] aspect-square relative" data-id="element-9">
          <div className="absolute inset-4 rounded-full opacity-30 -z-10" style={{
          backgroundColor: 'rgb(0, 180, 216)',
          filter: 'blur(40px)'
        }} data-id="element-10" />
          <img src="https://cdn.magicpatterns.com/uploads/v7HbDBttB6KwqMN8KX8fXz/hf_20260408_171512_ee149707-ab8e-4b03-a52c-19e854e7886e_(1)-Photoroom.webp" alt="Megapack Estimulación Temprana — Sistema Completo 0 a 36 meses" width="400" height="400" fetchPriority="high" decoding="sync" className="block max-w-full w-full h-auto object-contain relative z-10" style={{
          transform: 'rotateY(-4deg) rotateX(2deg)'
        }} data-id="element-11" />
        </div>

        {/* ── 4. TODO LO DEMÁS ── */}

        {/* Identidad profesional */}
        <p className="max-w-xl text-base sm:text-lg font-semibold text-gray-700 leading-7 m-0 mb-4" data-id="element-12">
          El sistema que las estimuladoras más organizadas de Argentina usan
          para planificar con criterio clínico, documentar el progreso de cada
          paciente y{' '}
          <span className="text-gray-900 font-black" data-id="element-13">
            cobrar lo que realmente vale su trabajo.
          </span>
        </p>

        {/* Tagline mecanismo */}
        <p className="max-w-xl text-lg sm:text-xl font-black text-cyan-brand m-0 mb-6" data-id="element-14">
          "144 sesiones listas. Criterio clínico real.
          <br className="hidden sm:block" data-id="element-15" />
          Sin buscar más, sin improvisar más."
        </p>

        {/* Tags */}
        <div className="mb-7 flex max-w-lg flex-wrap items-center justify-center gap-2" data-id="element-16">
          {['📄 100% PDF', '⚡ Acceso Inmediato', '🇦🇷 Argentina', '👶 0 a 36 meses', '✅ Criterio Clínico', '🧠 Neurodesarrollo'].map(tag => <span key={tag} className="whitespace-nowrap bg-white/80 text-xs sm:text-sm font-semibold text-gray-900 leading-5 border border-cyan-brand/30 rounded-full px-3 py-1 shadow-sm" data-id="element-17">
              {tag}
            </span>)}
        </div>

        {/* CTA */}
        <a href="#pricing" onClick={() => {
        if (typeof window !== 'undefined' && (window as any).fbq) {
          ;
          (window as any).fbq('track', 'Lead', {
            content_name: 'Hero CTA Click',
            content_category: 'Estimulación Temprana'
          });
        }
      }} className="block w-full max-w-sm bg-cyan-brand text-white text-lg sm:text-xl font-black rounded-xl px-6 sm:px-8 py-4 mb-4 no-underline text-center shadow-lg hover:opacity-90 transition-opacity" data-id="element-18">
          👶 → QUIERO MI MEGAPACK AHORA
        </a>

        {/* Trust Badges */}
        <div className="flex flex-row flex-wrap items-center justify-center gap-3 sm:gap-4 m-0" data-id="element-19">
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-20">
            🛡️ Garantía 7 días
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-21">
            🔒 Pago seguro
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-22">
            💳 Mercado Pago | Tarjeta | Transferencia
          </span>
        </div>
      </header>

      <main data-id="element-23">
        <SocialProof data-id="element-24" />
        <PainPoints data-id="element-25" />
        <Suspense fallback={null} data-id="element-26">
          <ProductShowcase data-id="element-27" />
        </Suspense>

        <Suspense fallback={null} data-id="element-28">
          <WhatYouGet data-id="element-29" />
          <Bonuses data-id="element-30" />
          <Countdown data-id="element-31" />
          <Pricing data-id="element-32" />
          <ProBusiness data-id="element-33" />
          <Guarantee data-id="element-34" />
          <Testimonials data-id="element-35" />
          <FAQ data-id="element-36" />
          <DarkCTA data-id="element-37" />
          <FinalCTA data-id="element-38" />
        </Suspense>
      </main>

      <Suspense fallback={null} data-id="element-39">
        <Footer data-id="element-40" />
        <PurchaseNotifications data-id="element-41" />
      </Suspense>
    </>;
}